import {
  createSignal,
  createEffect,
  on,
  onMount,
  onCleanup,
  Suspense,
  lazy,
  For,
  Switch,
  Show,
  createUniqueId,
  createMemo,
  splitProps,
} from 'solid-js';
import * as tree from '@zag-js/tree-view';
import { normalizeProps, useMachine } from '@zag-js/solid';
import { CaretRight, CaretDown } from 'phosphor-solid';
import {
  DataProvider,
  LayerProvider,
  useConfig,
  Icon,
  specTrack,
  useEventBus,
  useTheme,
} from 'framework';

import { LoadIcon, LoadSplash, calcGridItemStyle } from 'utils';
import { set } from 'radash';
import { create } from 'domain';
import { appendFile } from 'fs';

import('./node.css');

const DeckMapContainer = lazy(() => import('components/deckmap'));
const VegaContainer = lazy(() => import('components/vegapanel'));
const DocContainer = lazy(() => import('components/docpanel'));

const createMenu = (menuLayout, nodes) => {
  const menu = {
    id: 'ROOT',
    name: '',
    children: [],
  };
  if (!!menuLayout && !!nodes && menuLayout.length == nodes.length) {
    menuLayout.forEach((menuItem, index) => {
      const [root, label] = Array.isArray(menuItem.label)
        ? menuItem.label
        : [menuItem.label, null];
      const [rootIcon, labelIcon] = Array.isArray(menuItem.icon)
        ? menuItem.icon
        : [menuItem.icon, null];
      if (label) {
        let parent = menu.children.find((n) => n.label == root);
        if (!parent) {
          parent = {
            id: root,
            label: root,
            icon: rootIcon,
            color: menuItem.color,
            children: [],
          };
          menu.children.push(parent);
        }
        parent.children.push({
          id: nodes[index].id,
          label: label,
          icon: labelIcon,
          color: menuItem.color,
          children: [], // Use empty array instead of null
        });
      } else {
        menu.children.push({
          id: nodes[index].id,
          label: root,
          icon: rootIcon,
          color: menuItem.color,
          children: [], // Use empty array instead of null
        });
      }
    });
  }
  return tree.collection({
    rootNode: menu,
    nodeToValue: (item) => item.id,
    nodeToString: (item) => item.label,
    nodeToChildren: (item) => item?.children || [],
  });
};

const MenuLabel = (props) => {
  const theme = useTheme();
  return (
    <div
      style={{ display: 'flex', color: props.color || theme().textColor }}
      class="menu-label"
    >
      <Show when={props.icon}>
        <Icon
          url={props.icon}
          size={16}
          color={props.color || theme().textColor}
        />
      </Show>
      <span>{props.label}</span>
    </div>
  );
};

const MenuItem = (props) => {
  //const { item, indexPath, api, activeItem } = props;
  const itemProps = { indexPath: props.indexPath, node: props.item };
  const itemState = createMemo(() => {
    try {
      return props.api().getNodeState(itemProps);
    } catch (error) {
      console.error('Error getting node state:', error, props.item);
      return { isBranch: false, expanded: false };
    }
  });
  const active = createMemo(
    () =>
      props.item.id == props.activeItem() ||
      (itemState().isBranch &&
        !itemState().expanded &&
        props.item.children &&
        Array.isArray(props.item.children) &&
        props.item.children.some((c) => c && c.id == props.activeItem())),
  );
  return (
    <Show
      when={itemState().isBranch}
      fallback={
        <div
          {...props.api().getItemProps(itemProps)}
          class={active() ? 'active' : ''}
        >
          <MenuLabel
            label={props.item.label}
            icon={props.item.icon}
            color={props.item.color}
          />
        </div>
      }
    >
      <div
        {...props.api().getBranchProps(itemProps)}
        class={active() ? 'active' : ''}
        onMouseOver={() =>
          props.mouseOver && props.api().setExpandedValue([props.item.id])
        }
        onMouseOut={() => props.mouseOver && props.api().setExpandedValue([])}
      >
        <div {...props.api().getBranchControlProps(itemProps)}>
          <div {...props.api().getBranchIndicatorProps(itemProps)}>
            <MenuLabel
              label={props.item.label}
              icon={props.item.icon}
              color={props.item.color}
            />
            <CaretRight />
          </div>
        </div>
        <div
          {...props.api().getBranchContentProps(itemProps)}
          onMouseOver={() =>
            props.mouseOver && props.api().setExpandedValue([props.item.id])
          }
          onMouseOut={() => props.mouseOver && props.api().setExpandedValue([])}
        >
          <div {...props.api().getBranchIndentGuideProps(itemProps)} />
          <Show
            when={props.item.children && Array.isArray(props.item.children)}
          >
            <Index each={props.item.children}>
              {(childItem, index) => (
                <MenuItem
                  item={childItem()}
                  indexPath={[...props.indexPath, index]}
                  api={props.api}
                  activeItem={props.activeItem}
                  mouseOver={props.mouseOver}
                />
              )}
            </Index>
          </Show>
        </div>
      </div>
    </Show>
  );
};

const MenuContainer = (ns) => {
  const { triggerEvent } = useEventBus();
  const theme = useTheme();
  const [menu, setMenu] = createSignal(createMenu(ns.menuLayout, ns.children));
  const [activeNode, setActiveNode] = createSignal(ns.children[0]);
  const [selectedValue, setSelectedValue] = createSignal([]);
  const [expandedItems, setExpandedItems] = createSignal([]);
  const item = createMemo(() => activeNode() && activeNode().id);

  // Use a stable ID for the machine to ensure it doesn't recreate on every render
  const machineId = createMemo(() => ns.id || `menu-${ns.path}`);

  const service = useMachine(tree.machine, {
    id: machineId(),
    collection: menu(),
    onSelectionChange: (details) => {
      console.log(details);
      setSelectedValue(details.selectedValue);
    },
    onExpandedChange: (details) => {
      setExpandedItems(details.expandedValue);
    },
  });

  const api = createMemo(() => tree.connect(service, normalizeProps));
  const obs = new MutationObserver(function () {
    window.dispatchEvent(new Event('resize'));
  });
  let menuRoot;

  // Initialize the active node and selection when component mounts
  createEffect(() => {
    if (ns.children && ns.children.length > 0) {
      // Set initial active node if not already set
      if (!activeNode()) {
        setActiveNode(ns.children[0]);
      }

      // Set initial selection if not already set
      if (selectedValue().length === 0 && activeNode()) {
        api().setSelectedValue([activeNode().id]);
      }
    }
  });

  createEffect(
    on(selectedValue, () => {
      const activeValue = selectedValue()[0];
      if (!activeValue) return;
      const node = ns.children.find((n) => n.id == activeValue);
      if (node) {
        setActiveNode(node);
        // Trigger window resize event after tab change to ensure components render correctly
        setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
      } else {
        api().expand();
      }
    }),
  );
  createEffect(
    on(
      () => specTrack(ns.activeItem),
      () => {
        if (ns.activeItem) {
          api().setSelectedValue([ns.activeItem]);
          // Trigger window resize event after programmatic tab change
          setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
        }
      },
    ),
  );
  createEffect(
    on(
      () => [specTrack(ns.menuLayout), specTrack(ns.children)],
      () => {
        try {
          if (!ns.menuLayout || !ns.children) {
            console.warn('Menu layout or children is undefined');
            return;
          }

          // Create a new menu collection
          const newmenu = createMenu(ns.menuLayout, ns.children);

          // Update the menu signal
          setMenu(newmenu);

          // Directly update the collection in the API
          if (api() && api().collection) {
            api().collection = newmenu;
          }

          // Force the tree-view to update by sending a SYNC event
          // This is needed because changing the menu signal doesn't automatically
          // update the tree-view's internal collection
          setTimeout(() => {
            try {
              service.send({ type: 'SYNC' });

              // If there's a selected value, make sure it's still valid
              if (selectedValue().length > 0) {
                const currentValue = selectedValue()[0];
                const stillValid = ns.children.some(
                  (n) => n.id === currentValue,
                );
                if (!stillValid && ns.children.length > 0) {
                  api().setSelectedValue([ns.children[0].id]);
                }
              }
            } catch (innerError) {
              console.error('Error syncing tree-view:', innerError);
            }
          }, 0);

          // If children changed, we may need to update the active node
          if (ns.children && ns.children.length > 0) {
            // If current active node is no longer in children, reset to first child
            const currentActiveId = activeNode()?.id;
            const nodeStillExists = ns.children.some(
              (n) => n.id === currentActiveId,
            );

            if (!nodeStillExists) {
              setActiveNode(ns.children[0]);
            }
          }
        } catch (error) {
          console.error('Error updating menu:', error);
        }
      },
    ),
  );
  createEffect(() => {
    triggerEvent({
      control: null,
      action: 'changeMenuItem',
      source: `${ns.path}`,
      pos: null,
      object: null,
      data: JSON.parse(JSON.stringify(item())),
    });
  });
  onMount(() => {
    obs.observe(menuRoot, {
      subtree: true,
      attributes: true,
      childList: true,
      attributeFilter: ['data-selected'],
    });
  });
  onCleanup(() => {
    obs.disconnect();
  });
  const orientation = createMemo(() => {
    return (ns.layout == 'left') | (ns.layout == 'right')
      ? 'vertical'
      : 'horizontal';
  });
  const order = createMemo(() => {
    return (ns.layout == 'bottom') | (ns.layout == 'right') ? 'last' : 'first';
  });
  const [id, type, spec] = splitProps(activeNode, [
    'id',
    'nodeType',
    'nodeSpec',
  ]);

  return (
    <div
      ref={menuRoot}
      class={`menu-node-container menu-node-${orientation()}`}
    >
      {order() == 'last' && activeNode() ? (
        <div className="menu-node-content">
          <Node
            id={id}
            nodeType={type}
            nodeSpec={spec}
            path={`${ns.path}/children/#${id}`}
          />
        </div>
      ) : null}
      <div {...api().getRootProps()}>
        <div {...api().getTreeProps()} class={`menu-root-${orientation()}`}>
          <Index each={menu().rootNode.children}>
            {(_item, index) => (
              <MenuItem
                item={_item()}
                indexPath={[index]}
                api={api}
                activeItem={item}
                mouseOver={ns.openOnMouseOver}
              />
            )}
          </Index>
        </div>
      </div>
      {order() == 'first' && activeNode() ? (
        <div className="menu-node-content">
          <Node
            id={activeNode().id}
            nodeType={activeNode().nodeType}
            nodeSpec={activeNode()}
            path={`${ns.path}/children/#${activeNode().id}`}
          />
        </div>
      ) : null}
    </div>
  );
};

const NodeContainer = (n) => {
  return (
    <Switch>
      <Match when={n.nodeType == 'world'}>
        <LayerProvider layers={n.nodeSpec.children.filter((c) => c.nodeType == 'worldlayer')}>
          <Suspense fallback={<LoadSplash />}>
            <DeckMapContainer id={n.id} spec={n.nodeSpec} path={n.path} />
          </Suspense>
        </LayerProvider>
      </Match>
      <Match when={n.nodeType == 'plot'}>
        <Suspense fallback={<LoadSplash />}>
          <VegaContainer id={n.id} spec={n.nodeSpec} path={n.path} />
        </Suspense>
      </Match>
      <Match when={n.nodeType == 'document'}>
        <Suspense fallback={<LoadSplash />}>
          <DocContainer id={n.id} spec={n.nodeSpec} path={n.path} />
        </Suspense>
      </Match>
    </Switch>
  );
};

export const Node = (spec) => {
  const [height, setHeight] = createSignal(0);
  const [width, setWidth] = createSignal(0);
  let ref;

  const handleResize = () => {
    setHeight(ref?.offsetHeight);
    setWidth(ref?.offsetWidth);
  };

  onMount(() => {
    window.addEventListener('resize', handleResize);
    // Delay initial measurement to ensure layout is complete
    requestAnimationFrame(() => handleResize());
  });

  onCleanup(() => {
    window.removeEventListener('resize', handleResize);
  });

  if (spec.nodeType == 'grid') {
    if (spec.nodeSpec.children.length != spec.nodeSpec.gridLayout.length) {
      throw new Error('Grid must have same number of children as grid layout');
    }
  }

  return (
    <Switch
      fallback={
        <NodeContainer
          id={spec.id}
          path={spec.path}
          nodeType={spec.nodeType}
          nodeSpec={spec.nodeSpec}
        />
      }
    >
      <Match when={spec.nodeType == 'grid'}>
        <div className="grid-wrapper" ref={el => ref = el}>
          <For each={spec.nodeSpec.children}>
            {(n, i) => (
              <div
                className="grid-node"
                style={{
                  ...calcGridItemStyle(
                    spec.nodeSpec.gridSize.columns,
                    spec.nodeSpec.gridSize.rows,
                    height(),
                    width(),
                    spec.nodeSpec.gridLayout[i()],
                  )
                }}
              >
                <div class="grid-node-content">
                  <Node
                    id={n.id}
                    nodeType={n.nodeType}
                    nodeSpec={n}
                    path={`${spec.path}/children/${i()}`}
                  />
                </div>
              </div>
            )}
          </For>
        </div>
      </Match>
      <Match when={spec.nodeType == 'menu'}>
        <div ref={ref} className="menu-node">
          <MenuContainer
            ref={ref}
            activeItem={spec.nodeSpec.activeItem}
            open={spec.nodeSpec.open}
            layout={spec.nodeSpec.layout}
            children={spec.nodeSpec.children}
            openOnMouseOver={spec.nodeSpec.openOnMouseOver}
            path={spec.path}
            menuLayout={spec.nodeSpec.menuLayout}
          />
        </div>
      </Match>
    </Switch>
  );
};
